import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ApiResponse } from '../interfaces/common.interfaces';
import {
  ClaimsRequest,
  ClaimsData,
  LossRatioResponse,
  LossRatioRequest,
  ReembolsosRequest,
  ReembolsosResponse,
  ReembolsoSaveRequest,
  ReembolsoSaveResponse,
  UploadFileRequest,
  UploadResponse,
  ClaimOrderResponse,
  ClaimOrderRequest,
  CustomerInformationResponse,
  CustomerInformationRequest,
} from '../interfaces/reclamaciones.interface';

/**
 * Servicio para gestionar las operaciones relacionadas con reclamaciones y reembolsos
 */
@Injectable({
  providedIn: 'root',
})
export class ReclamacionesService {
  private apiUrl = environment.int365_dom + '/api/v1';

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Obtiene la lista de reclamaciones según los criterios especificados
   * @param request Objeto con los parámetros de búsqueda de reclamaciones
   * @returns Observable con la respuesta de la API conteniendo el array de reclamaciones
   */
  getClaims(
    request: ClaimsRequest
  ): Observable<HttpResponse<ApiResponse<ClaimsData>>> {
    return this.http.get<ApiResponse<ClaimsData>>(
      `${this.apiUrl}/Claims/claims`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Obtiene información de reembolsos según los criterios especificados
   * @param request Objeto con los parámetros de búsqueda de reembolsos
   * @returns Observable con la respuesta conteniendo la información de reembolsos
   */
  getReembolsos(request: ReembolsosRequest): Observable<ReembolsosResponse> {
    return this.http
      .get<ReembolsosResponse>(
        `${this.apiUrl}/refund/information`,
        this.getHttpOptions(request)
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de reembolsos:', error);


            throw error;
          }
        })
      );
  }

  /**
   * Obtiene información completa del cliente
   * @param request Objeto con los parámetros para obtener información del cliente
   * @returns Observable con la respuesta conteniendo la información del cliente
   */
  getCustomerInformation(
    request: CustomerInformationRequest
  ): Observable<CustomerInformationResponse> {
    return this.http
      .get<CustomerInformationResponse>(
        `${this.apiUrl}/refund/completecustomerinformation`,
        this.getHttpOptions(request)
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de reembolsos:', error);

            throw error;
          }
        })
      );
  }

  /**
   * Guarda una solicitud de reembolso
   * @param request Objeto con los datos de la solicitud de reembolso
   * @returns Observable con la respuesta de la operación de guardado
   */
  saveReembolso(
    request: ReembolsoSaveRequest
  ): Observable<ReembolsoSaveResponse> {
    return this.http
      .post<ReembolsoSaveResponse>(`${this.apiUrl}/refund/save`, request)
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error al guardar la solicitud de reembolso:', error);
            throw error;
          }
        })
      );
  }

  /**
   * Sube archivos asociados a una reclamación
   * @param request Objeto con el número de reclamación y los archivos a subir
   * @returns Observable con la respuesta de la operación de subida
   */
  uploadFile(request: UploadFileRequest): Observable<UploadResponse> {
    const params = new HttpParams().set(
      'saludCoreRequestId',
      request.ClaimNumber.toString()
    );
    const formData = new FormData();
    request.Files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
    return this.http
      .post<any>(`${this.apiUrl}/refund/uploadfile`, formData, { params })
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error al subir el documento:', error);
            throw error;
          }
        })
      );
  }

  /**
   * Obtiene las órdenes asociadas a una reclamación
   * @param request Objeto con los parámetros para obtener las órdenes
   * @returns Observable con la respuesta conteniendo el array de órdenes
   */
  getClaimOrders(
    request: ClaimOrderRequest
  ): Observable<ApiResponse<ClaimOrderResponse[]>> {
    return this.http
      .get<ApiResponse<ClaimOrderResponse[]>>(
        `${this.apiUrl}/Documents/ordersReport`,
        this.getHttpOptions(request)
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de órdenes:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }
            throw error;
          }
        })
      );
  }

  /**
   * Obtiene la relación de pérdidas (siniestralidad)
   * @param request Objeto con los parámetros para obtener la siniestralidad
   * @returns Observable con la respuesta conteniendo el array de datos de siniestralidad
   */
  getLossRatio(
    request: LossRatioRequest
  ): Observable<HttpResponse<ApiResponse<LossRatioResponse[]>>> {
    return this.http.get<ApiResponse<LossRatioResponse[]>>(
      `${this.apiUrl}/LossRatio/lossratio`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Configura las opciones HTTP incluyendo headers y parámetros
   * @param params Parámetros opcionales para la petición
   * @returns Objeto con las opciones HTTP configuradas
   * @throws Error si no se encuentra el token de autenticación
   */
  private getHttpOptions(params?: any) {
    const token = this.authService.getToken();
    if (!token) throw new Error('No token found');

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  /**
   * Formatea los parámetros para las peticiones HTTP
   * @param params Objeto con los parámetros a formatear
   * @returns Objeto con los parámetros formateados como strings
   */
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }
}
